import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "./ui/select";
import { cn } from '@/lib/utils';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧', abbr: 'EN' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', abbr: 'IT' },
  { code: 'es', label: 'Español', flag: '🇪🇸', abbr: 'ES' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', abbr: 'DE' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', abbr: 'FR' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷', abbr: 'EL' },
  { code: 'pt', label: 'Português', flag: '🇵🇹', abbr: 'PT' },
  { code: 'zh', label: '中文', flag: '🇨🇳', abbr: 'ZH' },
  { code: 'ja', label: '日本語', flag: '🇯🇵', abbr: 'JA' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺', abbr: 'RU' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷', abbr: 'FA' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', abbr: 'AR' },
];

export function LanguageSelector({ value, onChange, className, variant = 'default' }) {
  const { i18n } = useTranslation();
  
  const currentLang = value || i18n.language || 'en';
  const activeLangCode = currentLang.split(/[-_]/)[0].toLowerCase();
  const activeLang = LANGUAGES.find(l => l.code === activeLangCode) || LANGUAGES[0];

  const handleValueChange = (newValue) => {
    if (onChange) {
      onChange(newValue);
    } else {
      i18n.changeLanguage(newValue);
    }
  };

  const isCube = variant === 'cube';
  const isGlass = variant === 'glass' || isCube;

  return (
    <div className={cn("relative group/lang-selector", className)}>
      <Select value={activeLangCode} onValueChange={handleValueChange}>
        <SelectTrigger 
          className={cn(
            "transition-all duration-300 border font-medium flex items-center justify-between shadow-none ring-offset-background",
            isGlass 
              ? "bg-white/10 dark:bg-black/20 backdrop-blur-xl border-white/20 text-white hover:bg-white/20 hover:border-white/30" 
              : "bg-transparent border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 hover:border-slate-300 dark:hover:border-slate-700",
            isCube 
              ? "w-[60px] h-[34px] p-0 px-2 rounded-lg justify-center" 
              : "w-full min-w-[170px] h-10 px-3.5 rounded-xl",
          )}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-xl shrink-0 transition-transform duration-300 group-hover/lang-selector:scale-105">
              {activeLang.flag}
            </span>
            {!isCube && (
              <span className="truncate text-xs font-bold tracking-wider uppercase opacity-90">
                {activeLang.label}
              </span>
            )}
          </div>
          
          {!isCube && (
            <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 opacity-40 transition-transform duration-300 group-data-[state=open]:rotate-180" />
          )}
        </SelectTrigger>
        
        <SelectContent className={cn(
          "max-h-[380px] min-w-[240px] rounded-3xl overflow-hidden p-1.5 border shadow-[0_20px_50px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] animate-in fade-in zoom-in-95 duration-200",
          isGlass 
            ? "bg-slate-900/90 dark:bg-black/90 backdrop-blur-3xl border-white/10 text-white" 
            : "bg-white/95 dark:bg-slate-950/95 backdrop-blur-3xl border-slate-100 dark:border-slate-900 text-slate-900 dark:text-slate-100"
        )}>
          <div className="space-y-0.5">
            {LANGUAGES.map((lang) => {
              const isSelected = activeLangCode === lang.code;
              return (
                <SelectItem
                  key={lang.code}
                  value={lang.code}
                  className={cn(
                    "relative cursor-pointer rounded-2xl py-3 px-4 transition-all duration-200 outline-none select-none my-0.5",
                    "hover:bg-slate-500/10 focus:bg-slate-500/10",
                    isSelected && (isGlass
                      ? "bg-white/15 text-white"
                      : "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                    )
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl transition-transform duration-200 group-hover:scale-105">{lang.flag}</span>
                      <div className="flex flex-col">
                        <span className={cn(
                          "text-sm font-bold tracking-tight",
                          isSelected ? "opacity-100" : "opacity-60"
                        )}>{lang.label}</span>
                        <span className="text-[9px] font-black tracking-widest uppercase opacity-30">{lang.abbr}</span>
                      </div>
                    </div>
                    
                    {isSelected && (
                      <Check className="w-4 h-4 text-current stroke-[3px]" />
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
}




